import { ColumnsType } from '@agro-club/agroclub-shared'

import { isRussia, REACT_APP_INSTANCE_ID } from 'env'
import useFormatDate from 'hooks/useFormatDate'
import { DASH, NOT_AVAILABLE_URL, PHONE_TABLE_CELL_WIDTH } from 'modules/constants'
import { Task } from 'modules/domain/task/types'
import { UserShort } from 'modules/domain/types'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router'
import { LastCommentCell } from 'views/components/LastCommentCell/LastCommentCell'
import { ActionButtons } from 'views/components/Table/Cells/ActionButtons'
import { BlackListUserBtn } from 'views/components/TaskActions/BlackListUserBtn'
import { CompleteTaskBtn } from 'views/components/TaskActions/CompleteTask/CompleteTaskBtn'
import { KeyEntityIcon, UserHasDealsIcon } from 'views/components/TooltipIcons/TooltipIcons'
import { TruncatedValue } from 'views/components/TruncatedValue/TruncatedValue'
import DealRoutes from 'views/pages/Deal/routes'
import UserRoutes from 'views/pages/User/routes'
import { CompanySizeTag } from 'views/pages/User/UserDetail/CompanySizeTag'
import { EntityWithIconsWrapper } from 'views/styled/common'
import APhone from 'views/ui/Phone/APhone'
import { TableTextLink } from 'views/ui/TableTextLink/TableTextLink'
import { UseDashboardColumnsProps } from '../components/DashboardTasksList'

const getPathname = (row: Task) => {
  const userId = row?.linked_user?.id
  const dealId = row?.linked_deal?.id

  if (userId) {
    return generatePath(UserRoutes.Details, { id: userId })
  }

  if (dealId) {
    return generatePath(DealRoutes.Details, { id: dealId })
  }

  return NOT_AVAILABLE_URL
}

const defaultWidthOpt = {
  deadline: 150,
  status: 150,
  product_tags: 262,
  actions: 164,
  linked_user: 280,
  phone: PHONE_TABLE_CELL_WIDTH,
  assignee: 172,
  assignee_team: 112,
}

const widthOptions = {
  br: {
    deadline: 140,
  },
}

const overrideWidth = widthOptions[REACT_APP_INSTANCE_ID] || {}

const columnsWidth = { ...defaultWidthOpt, ...overrideWidth }

export const useCallsColumns = ({ successText, refetch }: UseDashboardColumnsProps): ColumnsType<any> => {
  const { t } = useTranslation()
  const formatDate = useFormatDate()

  return useMemo(
    () => [
      {
        title: t('dashboard:callBack'),
        dataIndex: 'deadline',
        width: columnsWidth.deadline,
        sorter: true,
        render: (deadline) => <>{formatDate(deadline, { withTime: true })}</>,
      },
      {
        title: t('dashboard:status'),
        dataIndex: 'linked_user',
        width: columnsWidth.status,
        render: (linked_user) =>
          linked_user?.company_size ? <CompanySizeTag companySize={linked_user.company_size} /> : DASH,
      },
      {
        title: t('common:user'),
        dataIndex: 'linked_user',
        autoWidth: columnsWidth.linked_user,
        render: (linked_user, record) => {
          // it is important to have state here for Task modal in Users
          const { pathname, search } = window.location
          return linked_user ? (
            <EntityWithIconsWrapper>
              <TruncatedValue hint={linked_user?.full_name}>
                <TableTextLink
                  id="user"
                  tableItemId={linked_user?.id}
                  state={{ pathname, search }}
                  to={getPathname(record)}
                >
                  {linked_user?.full_name}
                </TableTextLink>
              </TruncatedValue>
              <KeyEntityIcon isVisible={linked_user?.is_significant} />
              <UserHasDealsIcon isVisible={linked_user.has_shipped_deals} />
            </EntityWithIconsWrapper>
          ) : (
            DASH
          )
        },
      },
      {
        title: t('common:phone'),
        width: columnsWidth.phone,
        dataIndex: 'linked_user',
        render: (linked_user: UserShort) =>
          linked_user ? (
            <APhone phone={linked_user.phone} shortBtnTitle isMobileShort={linked_user.is_mobile_phone} />
          ) : (
            DASH
          ),
      },
      {
        title: t('user:list.tableHeaders.product_tags'),
        width: columnsWidth.product_tags,
        dataIndex: 'linked_user',
        key: 'product_tags',
        render: (linked_user?: UserShort) => linked_user?.product_tags?.map((t) => t.title).join(', '),
      },
      {
        title: t('user:list.tableHeaders.comments'),
        width: 331,
        dataIndex: 'comments',
        render: (_value, row) => <LastCommentCell user={row.linked_user} />,
      },
      {
        title: t('task:list.tableHeaders.assignee'),
        dataIndex: 'assignee',
        width: columnsWidth.assignee,
        render: (assignee) => assignee && <>{assignee.short_name}</>,
      },
      ...(isRussia
        ? [
            {
              title: t('task:list.tableHeaders.team'),
              width: columnsWidth.assignee_team,
              dataIndex: 'linked_user',
              render: (linked_user) => linked_user.team?.name,
            },
          ]
        : []),
      {
        title: t('task:list.tableHeaders.action'),
        width: columnsWidth.actions,
        dataIndex: 'id',
        render: (id, task) => {
          return (
            <ActionButtons>
              <CompleteTaskBtn refetch={refetch} task={task} successText={successText} forDashboard />
              <BlackListUserBtn refetch={refetch} userId={task?.linked_user?.id} forDashboard />
            </ActionButtons>
          )
        },
      },
    ],
    // make sure that we have latest refetch func
    [t, formatDate, successText, refetch],
  )
}
